import React, { useState } from 'react';
import { CopyIcon, MinusIcon, PlusIcon, Share2Icon } from 'lucide-react';
import { HUMANS } from '../../data/populations';
import { BRAND } from '../../data/brand';
import { useScenario } from '../../contexts/ScenarioContext';
import { formatRatio } from '../../utils/format';
import { SourceRef } from '../SourceRef';
interface Question {
  key: 'devices' | 'assistants' | 'bots';
  label: string;
  hint: string;
  max: number;
}
const QUESTIONS: Question[] = [{
  key: 'devices',
  label: 'Devices with a built-in assistant',
  hint: 'Phones, smart speakers, cars, TVs, watches',
  max: 30
}, {
  key: 'assistants',
  label: 'AI assistants you use',
  hint: 'ChatGPT, Gemini, Claude, Copilot, Meta AI…',
  max: 20
}, {
  key: 'bots',
  label: 'Bots or agents you’ve created',
  hint: 'Custom GPTs, characters, workflows, self-hosted agents',
  max: 200
}];
export function AgentRatio() {
  const [v, setV] = useState({
    devices: 2,
    assistants: 1,
    bots: 0
  });
  const [copied, setCopied] = useState(false);
  const {
    scenario
  } = useScenario();
  const avg = scenario.count / HUMANS.base;
  const n = v.devices + v.assistants + v.bots;
  const multiple = avg > 0 ? n / avg : 0;
  const share = `I command ${n} agent${n === 1 ? '' : 's'} — ${multiple >= 10 ? Math.round(multiple) : multiple.toFixed(1)}× the global average of ${formatRatio(avg)} agents per human (${scenario.label}). Count yours at ${BRAND.domain}`;
  const set = (k: Question['key'], val: number, max: number) => setV(p => ({
    ...p,
    [k]: Math.max(0, Math.min(max, val))
  }));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(share);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  const nativeShare = () => {
    if (navigator.share) navigator.share({
      text: share,
      url: BRAND.url
    }).catch(() => undefined);else copy();
  };
  return <section aria-labelledby="ratio-you-title" className="border-b border-line" data-id="element-48" data-mp="3ts585ef9mefp">
      <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-14 md:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-id="element-49" data-mp="1ze9ro0znccal">
        <div data-id="element-50" data-mp="1ukyivub9xlg1">
          <h2 id="ratio-you-title" className="font-serif text-3xl text-ink" data-id="element-51" data-mp="1ul85r910vchi">
            Your agent ratio
          </h2>
          <p className="mt-2 max-w-md text-[14px] text-muted" data-id="element-52" data-mp="1lltkcnzpps3v">Three questions. Each device assistant, AI service and bot you’ve made counts as one agent working for you, the same way the census counts them.</p>
          <div className="mt-6 divide-y divide-line border-y border-line" data-id="element-53" data-mp="2eas4fqneb4bt">
            {QUESTIONS.map(q => <div key={q.key} className="flex items-center justify-between gap-6 py-4" data-id="element-54" data-mp="29aw8meouqj7c">
                <div className="min-w-0" data-id="element-55" data-mp="24ckye2una6ol">
                  <label htmlFor={`q-${q.key}`} className="block text-[14.5px] text-ink" data-id="element-56" data-mp="24bt3yvcsaezw">
                    {q.label}
                  </label>
                  <p className="text-[12px] text-faint" data-id="element-57" data-mp="sx5wqlrhqqn5">{q.hint}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1" data-id="element-58" data-mp="szihf6omvf3w">
                  <button type="button" aria-label={`Decrease ${q.label}`} onClick={() => set(q.key, v[q.key] - 1, q.max)} className="flex h-8 w-8 items-center justify-center rounded-md border border-line text-ink transition-colors duration-150 hover:border-machine" data-id="element-59" data-mp="iusbkpq3zque">
                    <MinusIcon className="h-3.5 w-3.5" data-id="element-60" data-mp="xidjqy1ucg96" />
                  </button>
                  <input id={`q-${q.key}`} type="number" inputMode="numeric" min={0} max={q.max} value={v[q.key]} onChange={e => set(q.key, Number(e.target.value) || 0, q.max)} className="h-8 w-14 rounded-md border border-line bg-surface text-center font-mono text-[14px] text-ink focus:border-machine focus:outline-none" data-id="element-61" data-mp="11k8fln1061fk" />
                  <button type="button" aria-label={`Increase ${q.label}`} onClick={() => set(q.key, v[q.key] + 1, q.max)} className="flex h-8 w-8 items-center justify-center rounded-md border border-line text-ink transition-colors duration-150 hover:border-machine" data-id="element-62" data-mp="3i7uh15vh88kc">
                    <PlusIcon className="h-3.5 w-3.5" data-id="element-63" data-mp="17nbo4tz11r40" />
                  </button>
                </div>
              </div>)}
          </div>
        </div>

        <div className="flex flex-col justify-center" aria-live="polite" data-id="element-64" data-mp="1gm6y8h56zlcl">
          <p className="text-[14px] text-muted" data-id="element-65" data-mp="svb6tl4nes5v">You command</p>
          <p className="mt-1 font-mono text-[clamp(3.5rem,8vw,6rem)] leading-none tabular text-machine" data-id="element-66" data-mp="ouhmt2ubtxi9">{n}</p>
          <p className="mt-2 text-[15px] text-ink" data-id="element-67" data-mp="12lmffkza1pj3">
            agents vs a global average of <span className="font-mono" data-id="element-68" data-mp="12nqosify3gqr">{formatRatio(avg)}</span>
            <SourceRef id="fermi" estimate={{
            low: formatRatio(scenario.low / HUMANS.base),
            high: formatRatio(scenario.high / HUMANS.base)
          }} note={`${scenario.label} scenario ÷ world population.`} data-id="element-69" data-mp="xtmlc95w1quf" />
          </p>
          <div className="mt-6 h-2 rounded-full bg-raised" aria-hidden="true" data-id="element-70" data-mp="568kw12zed5k">
            <div className="h-full rounded-full bg-machine transition-[width] duration-200 ease-out" style={{
            width: `${Math.min(100, Math.log10(1 + n) / Math.log10(1 + Math.max(10, n)) * 100)}%`
          }} data-id="element-71" data-mp="2gsaoyyf7y3aw" />
          </div>
          <p className="mt-2 font-mono text-[12px] text-muted" data-id="element-72" data-mp="27v0a7336zw18">{n === 0 ? 'Zero — you’re outnumbered by everyone else’s machines.' : `${multiple >= 10 ? Math.round(multiple) : multiple.toFixed(1)}× the average human`}</p>

          <div className="mt-6 rounded-md border border-line bg-surface p-4" data-id="element-73" data-mp="34jg3995ignj3">
            <p className="text-[13.5px] leading-relaxed text-ink/90" data-id="element-74" data-mp="34hc1oxv340lz">{share}</p>
            <div className="mt-3 flex gap-2" data-id="element-75" data-mp="2qj5bm3wdn4wv">
              <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-[13px] text-ink transition-colors duration-150 hover:border-machine" data-id="element-76" data-mp="2vihy2njc69yx">
                <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-77" data-mp="1k44evvzj2ip8" /> {copied ? 'Copied' : 'Copy text'}
              </button>
              <button type="button" onClick={nativeShare} className="inline-flex items-center gap-1.5 rounded-md bg-machine px-3 py-1.5 text-[13px] font-medium text-bg transition-opacity duration-150 hover:opacity-90" data-id="element-78" data-mp="1006w2sy6l1t5">
                <Share2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-79" data-mp="r7c0kqyimkgk" /> Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}